import axios from "@/plugins/axios";
import qs from "qs";
import utils from "@/stores/utils";
import catalogentries from "./submodules/catalogentries";

const state = {
    bundle: null,
    bundle_loading: false,
    bundle_loading_errors: null,
    bundle_saving: false,
    bundle_saving_errors: {},
    bundle_deleting: false,
    bundle_deleting_errors: null,

    components: [],
    components_count: 0,
    components_loading: false,
    components_loading_errors: null,
    components_saving: false,
    components_saving_errors: {},

    component: null,
    component_loading: false,
    component_loading_errors: null,
    component_saving: false,
    component_saving_errors: {},

    bundle_stats: null,
    bundle_stats_loading: false,
    bundle_stats_loading_errors: null,

    order: [],
    order_loading: false,
    order_loading_errors: null,

    recalculating_cost: false,
};

const mutations = {
    // Bundle mutations
    updateBundle(state, bundle) {
        if (bundle && state.bundle && bundle?.id == state.bundle?.id) {
            state.bundle = bundle;
            return;
        }
        state.bundle = bundle;
        state.bundle_loading = false;
        state.bundle_loading_errors = null;
        state.bundle_saving = false;
        state.bundle_saving_errors = {};
        state.bundle_deleting = false;
        state.bundle_deleting_errors = null;
    },

    updateBundleLoading(state, loading) {
        state.bundle_loading = loading;
        if (loading) {
            state.bundle_loading_errors = null;
        }
    },

    updateBundleLoadingErrors(state, errors) {
        state.bundle_loading_errors = errors;
        state.bundle_loading = false;
    },

    updateBundleSaving(state, saving) {
        state.bundle_saving = saving;
        if (saving) {
            state.bundle_saving_errors = {};
        }
    },

    updateBundleSavingErrors(state, errors) {
        state.bundle_saving_errors = errors;
        state.bundle_saving = false;
    },

    updateBundleDeleting(state, deleting) {
        state.bundle_deleting = deleting;
        if (deleting) {
            state.bundle_deleting_errors = null;
        }
    },

    updateBundleDeletingErrors(state, errors) {
        state.bundle_deleting_errors = errors;
        state.bundle_deleting = false;
    },

    // Components mutations
    updateComponents(state, components) {
        // Ensure all components have the isEditing property
        const componentsWithEditing = components.map(component => ({
            ...component,
            isEditing: component.isEditing || false
        }));
        state.components = componentsWithEditing;
        state.components_loading = false;
        state.components_loading_errors = null;
    },

    updateComponentsLoading(state, loading) {
        state.components_loading = loading;
        if (loading) {
            state.components_loading_errors = null;
        }
    },

    updateComponentsLoadingErrors(state, errors) {
        state.components_loading_errors = errors;
        state.components_loading = false;
    },

    updateComponentsSaving(state, saving) {
        state.components_saving = saving;
        if (saving) {
            state.components_saving_errors = {};
        }
    },

    updateComponentsSavingErrors(state, errors) {
        state.components_saving_errors = errors;
        state.components_saving = false;
    },

    updateComponentsCount(state, count) {
        state.components_count = count;
    },

    // Mutations pour les composants temporaires
    addTempComponent(state, tempComponent) {
        state.components = [tempComponent, ...state.components];
        // Update the count in the bundle if available
        if (state.bundle) {
            state.bundle.components_count = (state.bundle.components_count || 0) + 1;
        }
    },

    removeTempComponent(state, tempId) {
        state.components = state.components.filter(component => component.id !== tempId);
        // Update the count in the bundle if available
        if (state.bundle) {
            state.bundle.components_count = Math.max(0, (state.bundle.components_count || 0) - 1);
        }
    },

    replaceTempComponent(state, { tempId, savedComponent }) {
        const index = state.components.findIndex(component => component.id === tempId);
        if (index !== -1) {
            state.components[index] = savedComponent;
            state.components[index].isNew = false;
            state.components[index].isEditing = false;
        }
    },

    // Mutations for component editing
    startEditComponent(state, componentId) {
        const componentIndex = state.components.findIndex(comp => comp.id === componentId);
        if (componentIndex !== -1) {
            state.components[componentIndex].isEditing = true;
        }
    },

    cancelEditComponent(state, componentId) {
        const component = state.components.find(comp => comp.id === componentId);
        if (component) {
            component.isEditing = false;
        }
    },

    // Component mutations
    setComponent(state, component) {
        state.component = component;
        state.component_loading = false;
        state.component_loading_errors = null;
    },

    setComponentLoading(state, loading) {
        state.component_loading = loading;
        if (loading) {
            state.component_loading_errors = null;
        }
    },

    setComponentLoadingErrors(state, errors) {
        state.component_loading_errors = errors;
        state.component_loading = false;
    },

    setComponentSaving(state, saving) {
        state.component_saving = saving;
        if (saving) {
            state.component_saving_errors = {};
        }
    },

    setComponentSavingErrors(state, errors) {
        state.component_saving_errors = errors;
        state.component_saving = false;
    },

    clearComponent(state) {
        state.component = null;
        state.component_loading_errors = null;
        state.component_saving_errors = {};
    },

    // Bundle stats mutations
    updateBundleStats(state, bundle_stats) {
        state.bundle_stats = bundle_stats;
        state.bundle_stats_loading_errors = null;
    },

    updateBundleStatsLoading(state, loading) {
        state.bundle_stats_loading = loading;
        if (loading) {
            state.bundle_stats_loading_errors = null;
        }
    },

    updateBundleStatsLoadingErrors(state, errors) {
        state.bundle_stats_loading_errors = errors;
    },

    // Order mutations
    updateOrder(state, order) {
        state.order = order;
        state.order_loading = false;
        state.order_loading_errors = null;
    },

    updateOrderLoading(state, loading) {
        state.order_loading = loading;
        if (loading) {
            state.order_loading_errors = null;
        }
    },

    updateOrderLoadingErrors(state, errors) {
        state.order_loading_errors = errors;
        state.order_loading = false;
    },

    // Recalculate cost mutations
    updateRecalculatingCost(state, recalculating) {
        state.recalculating_cost = recalculating;
    },
};

const actions = {
    // Initialisation
    async init({ commit, dispatch }, { bundle, components, stats }) {
        // Initialiser le bundle si fourni
        await commit('updateBundle', bundle);
        await commit('updateComponents', []);
        await commit('updateBundleStats', {});
        
        // Initialize catalogentries submodule with product filter if bundle exists
        if (bundle && bundle.product) {
            const productId = bundle.product;
            if (productId) {
                await dispatch("catalogentries/init", {
                    filters: { product: productId, ordering: 'product_name', limit: 100 },
                });
                await dispatch("catalogentries/fetchCatalogEntries");
            }
        }
        dispatch('fetchBundleStats');
        dispatch('fetchOrder');
        if (bundle) {
            await dispatch('fetchComponents');
        }
    },

    // Bundle actions
    async fetchBundle({ commit }, params) {
        commit('updateBundleLoading', true);
        try {
            const response = await axios.get(`/api/bundles/${state.bundle?.id}/`);
            commit('updateBundle', response.data);
            return response.data;
        } catch (error) {
            const errors = utils.handleError(error);
            commit('updateBundleLoadingErrors', errors);
            throw error;
        }
    },

    async updateBundle({ commit }, { id, ...bundleData }) {
        commit('updateBundleSaving', true);
        try {
            const response = await axios.put(`/api/bundles/${id}/`, bundleData);
            commit('updateBundle', response.data);
            return response.data;
        } catch (error) {
            const errors = utils.handleError(error);
            commit('updateBundleSavingErrors', errors);
            throw error;
        }
    },

    async deleteBundle({ commit }) {
        commit('updateBundleDeleting', true);
        try {
            await axios.post(`/api/bundles/${state.bundle.id}/archive/`);
            commit('updateBundle', null);
        } catch (error) {
            const errors = utils.handleError(error);
            commit('updateBundleDeletingErrors', errors);
            throw error;
        }
    },

    async uploadPhoto({ commit, state }, { file }) {
        commit('updateBundleSaving', true);
        try {
            const formData = new FormData();
            formData.append('photo', file);

            const response = await axios.post(
                `/api/bundles/${state.bundle.id}/upload_photo/`,
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                }
            );
            
            // Update bundle with new photo URL
            if (response.data.photo_url && state.bundle) {
                commit('updateBundle', {
                    ...state.bundle,
                    photo: response.data.photo_url
                });
            }
            
            return response.data;
        } catch (error) {
            const errors = utils.handleError(error);
            commit('updateBundleSavingErrors', errors);
            throw error;
        } finally {
            commit('updateBundleSaving', false);
        }
    },

    async deletePhotoAction({ commit, state }) {
        commit('updateBundleSaving', true);
        try {
            await axios.delete(`/api/bundles/${state.bundle.id}/delete_photo/`);
            
            // Update bundle to remove photo
            if (state.bundle) {
                commit('updateBundle', {
                    ...state.bundle,
                    photo: null
                });
            }
            
            return { success: true };
        } catch (error) {
            const errors = utils.handleError(error);
            commit('updateBundleSavingErrors', errors);
            throw error;
        } finally {
            commit('updateBundleSaving', false);
        }
    },

    // Components actions
    async fetchComponents({ commit }) {
        if (!state.bundle) {
            return;
        }
        commit('updateComponentsLoading', true);
        try {
            const response = await axios.get(`/api/bundles/${state.bundle?.id}/components/`, {params: {ordering: '-order', limit: 100}});
            commit('updateComponents', response.data.results);
            return response.data;
        } catch (error) {
            const errors = utils.handleError(error);
            commit('updateComponentsLoadingErrors', errors);
            throw error;
        }
    },

    async createComponent({ commit, state, dispatch }, { componentData }) {
        commit('updateComponentsSaving', true);
        try {
            const old_id = componentData.id;
            const response = await axios.post(`/api/bundles/${state.bundle.id}/components/`, componentData);
            // Add the new component at the beginning of the list
            let updatedComponents = [response.data, ...state.components];
            if (old_id) {
                updatedComponents = updatedComponents.filter(component => component.id !== old_id);
            }
            commit('updateComponents', updatedComponents);
            
            // Reload the bundle to update the total cost
            await dispatch('fetchBundle');
            await dispatch("catalogentries/fetchCatalogEntries");
            await dispatch('fetchBundleStats');
            
            return response.data;
        } catch (error) {
            const errors = utils.handleError(error);
            commit('updateComponentsSavingErrors', errors);
            throw error;
        }
    },

    async updateComponent({ commit, state, dispatch }, { componentId, componentData }) {
        commit('updateComponentsSaving', true);
        try {
            const response = await axios.put(`/api/bundles/${state.bundle.id}/components/${componentId}/`, componentData);
            // Update the component in the list
            const updatedComponents = state.components.map(component => 
                component.id === componentId ? response.data : component
            );
            commit('updateComponents', updatedComponents);
            
            // Reload the bundle to update the total cost
            await dispatch('fetchBundle');
            await dispatch('catalogentries/fetchCatalogEntries');
            await dispatch('fetchBundleStats');
            return response.data;
        } catch (error) {
            const errors = utils.handleError(error);
            commit('updateComponentsSavingErrors', errors);
            throw error;
        }
    },

    async deleteComponent({ commit, state, dispatch }, { componentId }) {
        commit('updateComponentsSaving', true);
        try {
            await axios.delete(`/api/bundles/${state.bundle.id}/components/${componentId}/`);
            // Remove the component from the list
            const filteredComponents = state.components.filter(component => component.id !== componentId);
            commit('updateComponents', filteredComponents);
            // Update the count in the bundle
            if (state.bundle) {
                commit('updateBundle', { ...state.bundle, components_count: Math.max(0, (state.bundle.components_count || 0) - 1) });
            }
            
            // Reload the bundle to update the total cost
            await dispatch('fetchBundle');
            await dispatch("catalogentries/fetchCatalogEntries");
            await dispatch('fetchBundleStats');
        } catch (error) {
            const errors = utils.handleError(error);
            commit('updateComponentsSavingErrors', errors);
            throw error;
        }
    },


    async activateBundle({ commit, dispatch }) {
        commit('updateBundleSaving', true);
        try {
            const response = await axios.post(`/api/bundles/${state.bundle.id}/activate/`);
            commit('updateBundle', response.data);
            dispatch('catalogentries/init', {
                filters: { product: state.bundle.product, ordering: 'product_name' },
            });
            return response.data;
        } catch (error) {
            const errors = utils.handleError(error);
            commit('updateBundleSavingErrors', errors);
            throw errors;
        } finally {
            commit('updateBundleSaving', false);
        }
    },

    async deactivateBundle({ commit }) {
        commit('updateBundleSaving', true);
        try {
            const response = await axios.post(`/api/bundles/${state.bundle.id}/deactivate/`);
            commit('updateBundle', response.data);
            return response.data;
        } catch (error) {
            const errors = utils.handleError(error);
            commit('updateBundleSavingErrors', errors);
            throw error;
        } finally {
            commit('updateBundleSaving', false);
        }
    },
    
    // Component actions
    async fetchComponent({ commit }, { componentId }) {
        commit('setComponentLoading', true);
        try {
            const response = await axios.get(`/api/bundles/${state.bundle?.id}/components/${componentId}/`);
            commit('setComponent', response.data);
            return response.data;
        } catch (error) {
            const errors = utils.handleError(error);
            commit('setComponentLoadingErrors', errors);
            throw error;
        }
    },

    clearComponent({ commit }) {
        commit('clearComponent');
    },

    // Stats actions
    async fetchBundleStats({ commit }) {
        commit('updateBundleStatsLoading', true);
        try {
            const response = await axios.get(`/api/bundles/${state.bundle?.id}/stats/`);
            commit('updateBundleStats', response.data);
            return response.data;
        } catch (error) {
            const errors = utils.handleError(error);
            commit('updateBundleStatsLoadingErrors', errors);
            throw error;
        } finally {
            commit('updateBundleStatsLoading', false);
        }
    },

    // Order actions
    async fetchOrder({ commit, state }) {
        if (!state.bundle || !state.bundle.active) {
            console.log('Bundle not active');
            return;
        }
        console.log('Fetching order');
        
        await commit('updateOrderLoading', true);
        try {
            const response = await axios.get(`/api/bundles/${state.bundle?.id}/production/`);
            await commit('updateOrder', response.data);
            return response.data;
        } catch (error) {
            const errors = utils.handleError(error);
            await commit('updateOrderLoadingErrors', errors);
            throw error;
        }
    },

    async saveCatalogEntryForBundle({ dispatch, state }, catalogEntry) {
        if (!state.bundle) {
            throw new Error('Bundle not loaded');
        }

        await dispatch('catalogentries/saveCatalogEntry', {
            instance: catalogEntry,
        });

        await dispatch('catalogentries/fetchCatalogEntries');
        await dispatch('fetchBundleStats');
    },

    // Recalculate bundle cost
    async recalculateBundleCost({ commit, state, dispatch }) {
        if (!state.bundle) {
            throw new Error('Bundle not loaded');
        }

        commit('updateRecalculatingCost', true);
        try {
            // Call the recalculate_cost API endpoint
            const response = await axios.post(
                `/api/bundles/${state.bundle.id}/recalculate_cost/`
            );

            // Update the bundle with the recalculated cost
            if (response.data && response.data.bundle) {
                commit('updateBundle', response.data.bundle);
            }

            // Refresh bundle stats and components
            await dispatch('fetchBundleStats');
            await dispatch('fetchComponents');

            return response.data;
        } catch (error) {
            const errors = utils.handleError(error);
            throw error;
        } finally {
            commit('updateRecalculatingCost', false);
        }
    },
};

const getters = {
    // Bundle getters
    bundle: state => {
        return state.bundle;
    },
    bundle_loading: state => {
        return state.bundle_loading;
    },
    bundle_loading_errors: state => {
        return state.bundle_loading_errors;
    },
    bundle_saving: state => {
        return state.bundle_saving;
    },
    bundle_saving_errors: state => {
        return state.bundle_saving_errors;
    },
    bundle_deleting: state => {
        return state.bundle_deleting;
    },
    bundle_deleting_errors: state => {
        return state.bundle_deleting_errors;
    },

    // Components getters
    components: state => {
        return state.components;
    },
    components_count: state => {
        return state.components_count;
    },
    components_loading: state => {
        return state.components_loading;
    },
    components_loading_errors: state => {
        return state.components_loading_errors;
    },
    components_saving: state => {
        return state.components_saving;
    },
    components_saving_errors: state => {
        return state.components_saving_errors;
    },

    // Component getters
    component: state => {
        return state.component;
    },
    component_loading: state => {
        return state.component_loading;
    },
    component_loading_errors: state => {
        return state.component_loading_errors;
    },
    component_saving: state => {
        return state.component_saving;
    },
    component_saving_errors: state => {
        return state.component_saving_errors;
    },

    // Bundle stats getters
    bundle_stats: state => {
        return state.bundle_stats;
    },
    bundle_stats_loading: state => {
        return state.bundle_stats_loading;
    },
    bundle_stats_loading_errors: state => {
        return state.bundle_stats_loading_errors;
    },

    // Order getters
    order: state => {
        return state.order;
    },
    order_loading: state => {
        return state.order_loading;
    },
    order_loading_errors: state => {
        return state.order_loading_errors;
    },

    // Recalculate cost getters
    recalculating_cost: state => {
        return state.recalculating_cost;
    },

    // Computed getters
    hasBundle: state => {
        const has = !!state.bundle;
        return has;
    },
    hasComponents: state => {
        const has = state.components.length > 0;
        return has;
    },
    componentsCount: state => {
        const count = state.bundle?.components_count || 0;
        return count;
    },
};

export default {
    namespaced: true,
    state,
    mutations,
    actions,
    getters,
    modules: {
        catalogentries: catalogentries({}),
    },
};
